import React, {useState, useEffect, useMemo, useCallback} from 'react'
import {Box, VStack, HStack, Link, useMultiStyleConfig, StackProps, LinkProps, TextProps, Skeleton} from '@chakra-ui/react'
import {Tag, Loader} from '_uiComponents'
import {useAnalytics, useViewport} from '_customHooks'
import {useProduct} from '@salesforce/commerce-sdk-react'
import useEinstein from '@salesforce/retail-react-app/app/hooks/use-einstein'
import {TILE_ASSET_SIZE, updateCloudinarySize} from '_utils'
import {getProductAnalyticsInfo, getProductImpressionAnalytics} from '_analytics/utils/analyticsUtils'
import {ProductImage} from './partials/product-image'
import {WishlistButton} from './partials/wishlist-button'
import {ProductOverlayWrapper} from './partials/product-overlay-wrapper'
import {ProductInfo} from './partials/product-info'
import {QuickAddButton} from './partials/quick-add-button'
import type {CategoryType, ProductType} from '_types'
import {ProductImagesCarousel} from './partials/product-images-carousel'

export interface ProductTileContext {
    type: 'category' | 'search' | 'recommendation' | 'other'
    category?: CategoryType
    searchText?: string
    recoUUID?: string
    recommenderName?: string
}

export interface ProductTileProps {
    product: ProductType
    idx?: number
    hasLazy?: boolean
    listingLayout?: 'grid3' | 'grid2' | 'wishlist' | 'shoptheimage'
    isFullWidth?: boolean
    analyticsList?: string
    shouldHideWishlistButton?: boolean
    variant?: 'wishlist' | 'shoptheimage'
    productTileContext?: ProductTileContext
    triggerImpression?: boolean
    onUpdateItemSize?: (variantId: string) => void
    onWishlistItemRemove?: () => void
    onAddProductToCart?: () => void
    showCarousel?: boolean
    showCover?: boolean
}

/**
 * @name ProductTile
 *
 * @component
 *
 * @description
 * Renders a product tile with various details such as image, price, wishlist button,
 * and quick add functionality. It also handles analytics events and supports features like size selection
 * and hover effects.
 *
 * @remarks
 * - It integrates with analytics for product impressions and quick cart actions.
 * - The wishlist functionality is handled via `useWishlistActions`.
 * - The component supports hover images, vertical videos, and size selection for products.
 *
 * @example
 * ```tsx
 * <ProductTile
 *   product={productData}
 *   idx={0}
 *   listingLayout="grid"
 * />
 * ```
 *
 * @param {ProductTileProps} props - Component properties
 * @param {ProductType} props.product - Product data object to display
 * @param {number} [props.idx=0] - Index of the product in a listing (used for analytics)
 * @param {boolean} [props.hasLazy] - Whether to lazy load images
 * @param {string} [props.listingLayout='grid3'] - Layout type ('grid2', 'grid3', 'full', 'horizontal')
 * @param {boolean} [props.isFullWidth] - Whether the tile should be full width
 * @param {string} [props.analyticsList] - List name for analytics
 * @param {boolean} [props.shouldHideWishlistButton] - Whether to hide the wishlist button
 * @param {string} [props.variant] - Variant type of the product tile (e.g., 'wishlist')
 * @param {function} [props.onWishlistItemRemove] - Callback to catch behaviour of wishlist item remove.
 * @param {function} [props.onAddProductToCart] - Callback to catch behaviour of add to cart item.
 *
 * @returns {JSX.Element} Rendered `ProductTile` component
 */
const ProductTile = React.memo(
    ({
        product,
        idx = 0,
        hasLazy,
        listingLayout = 'grid3',
        isFullWidth,
        analyticsList,
        shouldHideWishlistButton,
        variant,
        productTileContext = {type: 'other'},
        triggerImpression,
        onUpdateItemSize,
        onWishlistItemRemove,
        onAddProductToCart,
        showCarousel = true,
        showCover = true,
    }: ProductTileProps) => {
        const styles = useMultiStyleConfig('ProductTile', {listingLayout}) as Record<string, StackProps & LinkProps & TextProps>
        const {analyticsProductImpression, analyticsProductClick, analyticsEventQuickCartAdd} = useAnalytics()
        const {isMobile, isTablet, isDesktop} = useViewport()
        const einstein = useEinstein()

        const [shouldOpenOverlay, setShouldOpenOverlay] = useState<boolean | undefined>(undefined)
        const [selectedVariant, setSelectedVariant] = useState<string | null>(product.id)
        const [selectedSize, setSelectedSize] = useState<string | null>(null)
        const shouldUseProduct = useMemo(() => variant !== 'wishlist' && shouldOpenOverlay, [shouldOpenOverlay])
        const productRef = React.useRef<HTMLDivElement>(null)
        const quickAddButtonRef = React.useRef<HTMLButtonElement>(null)

        const {data: _product, isLoading} = useProduct(
            {
                parameters: {
                    id: selectedVariant,
                    c_productTile: true,
                    expand: ['availability'],
                },
            },
            {enabled: !!shouldUseProduct},
        )

        const customProduct = !isLoading && _product ? _product : product

        const {activeVariation, productCurrency, isProductInStock, isProductSet, productLink, productTextTag} = getProductAnalyticsInfo(customProduct)
        const deviceType = isMobile ? 'mobile' : isTablet ? 'tablet' : 'desktop'

        const tileAssetSize = useMemo(() => {
            return isFullWidth ? TILE_ASSET_SIZE[deviceType].full : TILE_ASSET_SIZE[deviceType][listingLayout]
        }, [isFullWidth, deviceType, listingLayout])

        const horizontalAssetSize = useMemo(() => {
            return TILE_ASSET_SIZE[deviceType].horizontal
        }, [deviceType])

        const productHorizontalImageSrc = product?.c_images?.horizontal?.url
        const productHorizontalImageAlt = product?.c_images?.horizontal?.alt
        const productHorizontalImage = useMemo(() => {
            return updateCloudinarySize(productHorizontalImageSrc, horizontalAssetSize)
        }, [productHorizontalImageSrc, horizontalAssetSize])

        const isHorizontalTile = Boolean(productHorizontalImage) && !isMobile && listingLayout === 'grid2' && isFullWidth

        const productVerticalVideoSrc = product?.c_videos?.vertical
        const hasCoverImage = showCover && Boolean((activeVariation?.images || customProduct?.c_images)?.cover)
        const showHoverImage = !productVerticalVideoSrc && !hasCoverImage
        const hasImageCarousel = showCarousel && Boolean((activeVariation?.images || customProduct?.c_images)?.showCarousel)

        const productImagesSrc = useMemo(() => {
            return (activeVariation?.images || customProduct?.c_images)?.large?.map((img) => img.url)
        }, [activeVariation?.images, customProduct?.c_images])

        const productImagesAlt = useMemo(() => {
            return (activeVariation?.images || customProduct?.c_images)?.large?.map((img) => img.alt)
        }, [activeVariation?.images, customProduct?.c_images])

        const productImages: Array<{src: string; alt: string}> = useMemo(() => {
            if (!productImagesSrc) return []
            const imagesToUse = hasImageCarousel ? productImagesSrc : productImagesSrc.slice(0, 1)
            return imagesToUse.map((imgSrc, index) => ({
                src: updateCloudinarySize(imgSrc, tileAssetSize),
                alt: productImagesAlt?.[index] || '',
            }))
        }, [productImagesSrc, productImagesAlt, tileAssetSize, hasImageCarousel])

        const productImageSrc =
            (showCover && activeVariation?.images?.cover) ||
            activeVariation?.images?.url ||
            activeVariation?.images?.large?.[0]?.url ||
            (showCover && customProduct?.c_images?.cover) ||
            customProduct?.c_images?.large?.[0]?.url

        const productImage: string = useMemo(() => {
            return !hasImageCarousel ? updateCloudinarySize(productImageSrc, tileAssetSize) : ''
        }, [productImageSrc, tileAssetSize])

        const productImageAlt = activeVariation?.images?.alt || activeVariation?.images?.large?.[0]?.alt || customProduct?.c_images?.large?.[0]?.alt

        // const productVerticalVideo = productVerticalVideoSrc && updateCloudinarySize(productVerticalVideoSrc, tileAssetSize) dpr is not applicable to video

        const hoverImageSrc = showHoverImage ? (activeVariation?.images || product?.c_images)?.large?.[1]?.url : undefined
        const hoverImage = useMemo(() => {
            return hoverImageSrc && updateCloudinarySize(hoverImageSrc, tileAssetSize)
        }, [hoverImageSrc, tileAssetSize])

        const toggleSizeSelector = useCallback(() => {
            setShouldOpenOverlay((prev) => {
                if (prev === false || prev === undefined) {
                    ;(document.querySelector('.openedSizeSelector') as HTMLElement)?.click()
                    quickAddButtonRef.current?.classList.add('openedSizeSelector')
                } else {
                    quickAddButtonRef.current?.classList.remove('openedSizeSelector')
                }
                return !prev
            })
        }, [])

        const sendAnalyticsImpression = () => {
            productRef.current?.setAttribute('data-impression', '')

            const analyticsProductObject = getProductImpressionAnalytics({
                product: customProduct,
                idx,
                tileAssetSize,
                isFullWidth,
                selectedSize,
                analyticsList,
                page: listingLayout === 'shoptheimage' ? 'editorial-shoptheimage' : 'plp',
            })

            analyticsProductImpression({
                currencyCode: productCurrency,
                template: listingLayout === 'shoptheimage' ? '' : listingLayout,
                impressions: analyticsProductObject,
            })
        }

        const onProductClick = () => {
            sendAnalyticsProductClick()

            // send einstein events
            const {type, category, searchText, recoUUID, recommenderName} = productTileContext

            if (type === 'search') {
                einstein.sendClickSearch(searchText, product)
            } else if (type === 'category') {
                einstein.sendClickCategory(category, product)
            } else if (type === 'recommendation') {
                einstein.sendClickReco({__recoUUID: recoUUID, recommenderName}, product)
            }
        }

        const sendAnalyticsProductClick = () => {
            const analyticsProductObject = getProductImpressionAnalytics({
                product: customProduct,
                idx,
                tileAssetSize,
                isFullWidth,
                selectedSize,
                analyticsList,
                page: listingLayout === 'shoptheimage' ? 'editorial-shoptheimage' : 'plp',
            })
            const {list, ...analyticsProductWithoutList} = analyticsProductObject

            analyticsProductClick({
                currencyCode: productCurrency,
                dimension25: listingLayout === 'shoptheimage' ? '' : listingLayout,
                actionField: {
                    list: list,
                },
                product: analyticsProductWithoutList,
            })
        }

        useEffect(() => {
            if (!productRef.current) {
                return
            }

            const observer = new IntersectionObserver(
                ([entry]) => {
                    if (!entry.isIntersecting || entry.intersectionRatio < 0.5) {
                        return
                    }

                    if (productRef.current?.getAttribute('data-impression') === '') {
                        observer.disconnect()
                        return
                    }

                    sendAnalyticsImpression()

                    observer.disconnect()
                },
                {
                    threshold: [0.5],
                },
            )

            observer.observe(productRef.current)
        }, [])

        useEffect(() => {
            if (triggerImpression) {
                sendAnalyticsImpression()
            }
        }, [triggerImpression])

        useEffect(() => {
            if (shouldOpenOverlay !== undefined) {
                const productId = customProduct?.productId || customProduct?.id

                analyticsEventQuickCartAdd({
                    accessType: 'tile',
                    action: shouldOpenOverlay ? 'open' : 'close',
                    label: productId,
                })
            }
        }, [shouldOpenOverlay])

        /**
         * Updates product in the wishlist when size is selected.
         */
        useEffect(() => {
            if (variant === 'wishlist' && selectedSize) {
                onUpdateItemSize?.(selectedSize)
            }
        }, [selectedSize])

        return (
            <>
                {isLoading && shouldUseProduct && <Loader />}
                <Box ref={productRef} {...styles.wrapper} data-testid={`product-tile-${product.productId}`}>
                    <Box {...styles.imageWrapper}>
                        <Link
                            href={productLink}
                            {...styles.image}
                            onClick={onProductClick}
                            tabIndex={-1}
                            aria-hidden={true}
                            aria-label={product.c_ariaLabel}
                            data-testid={`product-tile-${product.productId}-link`}
                        >
                            {hasImageCarousel && !isHorizontalTile ? (
                                <ProductImagesCarousel
                                    productImages={productImages}
                                    productId={customProduct.id}
                                    analyticsProductId={customProduct?.c_analytics?.productId}
                                    analyticsList={analyticsList}
                                    isDesktop={isDesktop}
                                    hasLazy={hasLazy}
                                    styles={styles}
                                    showHover={Boolean((activeVariation?.images || customProduct?.c_images)?.showHover)}
                                />
                            ) : (
                                <ProductImage
                                    productImage={productImage}
                                    hoverImage={hoverImage}
                                    videoSrc={productVerticalVideoSrc}
                                    isHorizontalTile={isHorizontalTile}
                                    isDesktop={isDesktop}
                                    alt={productImageAlt}
                                    hasLazy={hasLazy}
                                    horizontalImage={productHorizontalImage}
                                    horizontalImageAlt={productHorizontalImageAlt}
                                    styles={styles}
                                />
                            )}
                        </Link>

                        {productTextTag && (
                            <Box {...styles.tagLabel}>
                                <Tag variant="opacity">{productTextTag}</Tag>
                            </Box>
                        )}
                        {!isProductSet && !shouldHideWishlistButton && (
                            <WishlistButton
                                product={customProduct}
                                variant={variant}
                                selectedSize={selectedSize}
                                isMobile={isMobile}
                                isTablet={isTablet}
                                onWishlistItemRemove={onWishlistItemRemove}
                                styles={styles}
                            />
                        )}
                        {shouldOpenOverlay && (
                            <ProductOverlayWrapper
                                product={customProduct}
                                variant={variant}
                                selectedVariant={selectedVariant}
                                selectedSize={selectedSize}
                                setSelectedSize={setSelectedSize}
                                setSelectedVariant={setSelectedVariant}
                                toggleSizeSelector={toggleSizeSelector}
                                isInStock={isProductInStock}
                                productLink={productLink}
                                analyticsList={analyticsList}
                                idx={idx}
                                tileAssetSize={tileAssetSize}
                                isFullWidth={isFullWidth}
                                onAddProductToCart={onAddProductToCart}
                                onUpdateItemSize={onUpdateItemSize}
                            />
                        )}
                    </Box>
                    <HStack {...styles.body} {...(isHorizontalTile ? styles.horizontalBody : {})}>
                        <ProductInfo
                            product={product}
                            customProduct={customProduct}
                            styles={styles}
                            selectedSizeCode={selectedSize}
                            onProductClick={sendAnalyticsProductClick}
                        />
                        {!isProductSet && (
                            <QuickAddButton
                                ref={quickAddButtonRef}
                                onClick={() => toggleSizeSelector()}
                                shouldOpenOverlay={shouldOpenOverlay}
                                listingLayout={listingLayout}
                                productName={product.c_shortDescription}
                                productId={product.id}
                            />
                        )}
                    </HStack>
                </Box>
            </>
        )
    },
)

ProductTile.displayName = 'ProductTile'

const ProductSkeleton = () => {
    const styles = useMultiStyleConfig('ProductTile') as Record<string, StackProps & LinkProps & TextProps>

    return (
        <Box {...styles.wrapper} data-testid="product-tile-skeleton">
            <Box {...styles.skeletonImageWrapper}>
                <Skeleton {...styles.skeletonImage} />
            </Box>
            <HStack {...styles.body}>
                <VStack {...styles.details}>
                    <Skeleton {...styles.skeletonTitle} />
                    <Skeleton {...styles.skeletonPrice} />
                </VStack>
            </HStack>
        </Box>
    )
}

export {ProductTile, ProductSkeleton}
